import { useState, useRef, useEffect } from "react";

export default function HoverVideoPreview({ videoId, title, aspectRatio = "16/9" }) {
  const [iframeMounted, setIframeMounted] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(
    `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
  );

  useEffect(() => {
    setThumbnailUrl(`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`);
  }, [videoId]);

  const hoverTimer = useRef(null);
  const containerRef = useRef(null);
  const isMobile = window.matchMedia("(pointer: coarse)").matches;
  const paddingBottom = aspectRatio === "9/16" ? "177.78%" : "56.25%";

  const handleMouseEnter = () => {
    if (isMobile) return;
    hoverTimer.current = setTimeout(() => setIframeMounted(true), 200);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    clearTimeout(hoverTimer.current);
    setIframeMounted(false);
  };

  useEffect(() => {
    if (!isMobile) return;
    const container = containerRef.current;
    if (!container) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIframeMounted(true);
        else setIframeMounted(false);
      },
      { threshold: 0.7, rootMargin: "-20% 0px -25% 0px" }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [isMobile]);

  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${videoId}&modestbranding=1&rel=0&playsinline=1`;

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden cursor-none"
      style={{ paddingBottom }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <img
        src={thumbnailUrl}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        onError={() => setThumbnailUrl(`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`)}
      />

      <div
        className={`absolute inset-0 bg-carbon-950/20 group-hover:bg-carbon-950/20 transition-all duration-500 pointer-events-none z-10 ${iframeMounted ? "opacity-0" : "opacity-100"
          }`}
      />

      {iframeMounted && (
        <iframe
          key={videoId}
          src={embedUrl}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full border-0 z-20"
          style={{ animation: "fadeIn 0.6s ease forwards" }}
        />
      )}

      {iframeMounted && (
        <div className="absolute inset-0 cursor-pointer" style={{ zIndex: 25 }} />
      )}

      {!iframeMounted && !isMobile && (
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 pointer-events-none">
          <div className="flex items-center gap-2 bg-carbon-950/70 backdrop-blur-sm px-3 py-2 border border-carbon-700">
            <svg width="9" height="9" viewBox="0 0 9 9" fill="#ff6b35">
              <polygon points="1,0.5 8.5,4.5 1,8.5" />
            </svg>
            <span className="font-mono text-xs text-ash-300 tracking-widest uppercase">
              Hover to preview
            </span>
          </div>
        </div>
      )}
    </div>
  );
}